
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { selectPageInfo, setRows } from '../../common/components/PalmDataGrid/palmDataGridSlice';
import {
    deleteAclAccess
} from '../../common/services/api/Acls/AclAccesses.thunk';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function AclAccessDeleteDialogContent({
    title,
    itemId,
    editingRow,
}: Readonly<DeleteDialogContentProps<{ name: string }>>) {
    const { name } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const tenantDetails = useAppSelector(selectPageInfo);

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('ACL Access has been deleted.', { variant: 'success' });
    };

    const handleDeleteActionError = (error: { message: string; }) => {
        const errorCode = extractStatusCode(error.message);
        if (errorCode === 409) {
            enqueueSnackbar('The ACL Access needs to be removed from all ACLs prior to deletion.', { variant: 'error' });
        } else {
            handleDeleteDialogError('ACL Access', enqueueSnackbar, extractStatusCode(error.message));
        }
        setProcessing(false);
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const tenantId = params.tenantId;
    const aclAccessDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteAclAccess({
                aclAccessId: itemId.toString(),
                tenantId: tenantId ?? ''
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                    setProcessing(false);
                })
                .catch((error) => {
                    handleDeleteActionError(error);
                });
        }
    }, [itemId]);

    return (
        <DeleteDialog deleteCallback={aclAccessDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                ACL Access Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{tenantDetails?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
